// Simple settings store using localStorage so the UI works without a backend.
// Keys
const SETTINGS_KEY = 'rf.settings.v1';

const DEFAULTS = {
  allowApplyFromProperties: true,
  requiredDocuments: [
    'ID / Passport',
    'Proof of Income (3 months)',
    'Bank Statement (3 months)',
    'Proof of Residence',
  ],
  requiredDocumentsByProperty: {},
  manualCreationEnabled: true,
  paymentLink: '',
  bankDetails: {
    bank: '',
    accountName: '',
    accountNumber: '',
    branchCode: '',
    referenceFormat: 'PROPERTY-UNIT-NAME',
  },
};

export function getSettings() {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return { ...DEFAULTS };
    const parsed = JSON.parse(raw);
    return { ...DEFAULTS, ...(parsed || {}) };
  } catch (_) {
    return { ...DEFAULTS };
  }
}

export function saveSettings(patch) {
  const merged = { ...getSettings(), ...(patch || {}) };
  localStorage.setItem(SETTINGS_KEY, JSON.stringify(merged));
  return merged;
}

// Applicants/Tenants lightweight store
const APPLICANTS_KEY = 'rf.applicants.v1';
const TENANTS_KEY = 'rf.tenants.v1';

export function listApplicants() {
  try {
    return JSON.parse(localStorage.getItem(APPLICANTS_KEY)) || [];
  } catch {
    return [];
  }
}

export function saveApplicants(list) {
  localStorage.setItem(APPLICANTS_KEY, JSON.stringify(list || []));
}

export function addApplicant(app) {
  const list = listApplicants();
  const item = {
    id: crypto.randomUUID(),
    ref: generateRef('APP'),
    status: 'Applicant',
    createdAt: new Date().toISOString(),
    lastUpdated: new Date().toISOString(),
    events: [makeEvent('Applied')],
    ...(app || {}),
  };
  list.push(item);
  saveApplicants(list);
  return item;
}

export function listTenants() {
  try {
    return JSON.parse(localStorage.getItem(TENANTS_KEY)) || [];
  } catch {
    return [];
  }
}

export function saveTenants(list) {
  localStorage.setItem(TENANTS_KEY, JSON.stringify(list || []));
}

export function moveApplicantToTenant(applicantId, lease = {}) {
  const apps = listApplicants();
  const idx = apps.findIndex(a => a.id === applicantId);
  if (idx === -1) return null;
  const app = apps[idx];
  apps.splice(idx, 1);
  saveApplicants(apps);
  const tenant = {
    id: crypto.randomUUID(),
    ref: generateRef('TEN'),
    userId: app.userId || null,
    name: app.form?.fullName || app.name || '',
    email: app.form?.email || app.email || '',
    phone: app.form?.phone || app.phone || '',
    propertySlug: app.propertySlug,
    unitId: app.unitId || null,
    profilePhoto: app.profilePhoto || '',
    documents: app.documents || [],
    status: 'Active',
    lease: { status: 'Issued', ...lease, issuedAt: new Date().toISOString() },
    monthlyPrice: lease.monthlyPrice || app.monthlyPrice || null,
    createdAt: new Date().toISOString(),
    lastUpdated: new Date().toISOString(),
    events: [makeEvent('Lease Issued'), makeEvent('Approved')],
  };
  const tenants = listTenants();
  tenants.push(tenant);
  saveTenants(tenants);
  return tenant;
}

export function setTenantInactive(tenantId, reason = '') {
  const tenants = listTenants();
  const idx = tenants.findIndex(t => t.id === tenantId);
  if (idx === -1) return null;
  tenants[idx] = { ...tenants[idx], status: 'Inactive', inactiveReason: reason, inactiveAt: new Date().toISOString() };
  saveTenants(tenants);
  return tenants[idx];
}

// Updates and helpers
export function updateApplicant(id, patch) {
  const list = listApplicants();
  const idx = list.findIndex(a => a.id === id);
  if (idx === -1) return null;
  list[idx] = { ...list[idx], ...(patch || {}), lastUpdated: new Date().toISOString() };
  saveApplicants(list);
  return list[idx];
}

export function updateTenant(id, patch) {
  const list = listTenants();
  const idx = list.findIndex(t => t.id === id);
  if (idx === -1) return null;
  list[idx] = { ...list[idx], ...(patch || {}), lastUpdated: new Date().toISOString() };
  saveTenants(list);
  return list[idx];
}

export function addEvent(entity, id, label, data = {}) {
  const ev = makeEvent(label, data);
  if (entity === 'applicant') {
    const a = updateApplicant(id, { events: [...(updateApplicant._tmp || listApplicants().find(x=>x.id===id)?.events || []), ev] });
    return a;
  }
  if (entity === 'tenant') {
    const t = updateTenant(id, { events: [...(updateTenant._tmp || listTenants().find(x=>x.id===id)?.events || []), ev] });
    return t;
  }
  return null;
}

function makeEvent(label, data = {}) {
  return { id: crypto.randomUUID(), at: new Date().toISOString(), label, data };
}

export function generateRef(prefix = 'REF') {
  const now = new Date();
  const year = now.getFullYear();
  const seq = Math.floor(Math.random() * 9000) + 1000; // simple local seq
  return `${prefix}-${year}-${seq}`;
}

export function getRequiredDocsFor(propertySlug) {
  const s = getSettings();
  if (propertySlug && s.requiredDocumentsByProperty && s.requiredDocumentsByProperty[propertySlug]) {
    return s.requiredDocumentsByProperty[propertySlug];
  }
  return s.requiredDocuments || [];
}
